import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ILoyaltyGenerateOtpMutationVariables,
  useLoyaltyGenerateOtpMutation,
} from 'generated/graphql-gateway';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { StorageKeys } from 'utils/local-storage';

import { IShortCodeData, IUserShortCode, ShortCodeState } from './types';
import { differenceMsFromNow, isExpired } from './utils';

/**
 * useShortCode holds otp short-code and its state.
 */
export const useShortCode = (): IUserShortCode => {
  const [generateCodeOtp] = useLoyaltyGenerateOtpMutation({
    onCompleted: ({ generateOtp }) => {
      const { password: newShortCode, expirationTime } = generateOtp;

      setShortCodeData({
        shortCode: newShortCode,
        expiry: expirationTime,
      });
      setShortCodeState(ShortCodeState.Pending);
    },
    onError: () => {
      setRequestError();
    },
  });
  const generateCodeOtpTimeoutRef = useRef<NodeJS.Timeout | null>();
  const abortControllerRef = useRef<AbortController>();
  const [shortCodeLoading, setShortCodeLoading] = useState<boolean>(false);
  const [{ shortCode, expiry }, setShortCodeData, clearShortCodeData] = useLocalStorageState<
    IShortCodeData
  >({
    key: StorageKeys.IN_RESTAURANT_REDEMPTION_SHORT_CODE,
    defaultReturnValue: {},
  });
  const [shortCodeState, setShortCodeState] = useState<ShortCodeState>(() =>
    shortCode ? ShortCodeState.Pending : ShortCodeState.None
  );

  const setRequestError = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.RequestError);
  }, [clearShortCodeData]);

  const setShortCodeClaimed = useCallback(() => {
    setShortCodeState(ShortCodeState.Claimed);
  }, []);

  const setShortCodeExpired = useCallback(() => {
    setShortCodeState(ShortCodeState.Expired);
  }, []);

  const resetShortCode = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.None);
    setShortCodeLoading(false);
  }, [clearShortCodeData]);

  /**
   * getNewShortCode works as a debounce function in order to prevent running
   * multiple mutations at the same time
   */
  const getNewShortCode = async (params: ILoyaltyGenerateOtpMutationVariables) => {
    if (generateCodeOtpTimeoutRef.current) {
      clearTimeout(generateCodeOtpTimeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setShortCodeLoading(true);
    generateCodeOtpTimeoutRef.current = global.setTimeout(async () => {
      abortControllerRef.current = window.AbortController
        ? new window.AbortController()
        : undefined;

      try {
        await generateCodeOtp({
          variables: params,
          context: {
            fetchOptions: {
              signal: abortControllerRef.current?.signal,
            },
          },
        });
      } catch (_) {
        // do nothing here
      } finally {
        generateCodeOtpTimeoutRef.current = null;
        setShortCodeLoading(false);
      }
    }, 250);
  };

  useEffect(() => {
    if (!expiry) {
      return;
    } else if (isExpired(expiry)) {
      setShortCodeExpired();
      return;
    }

    const codeDurationMilliseconds = differenceMsFromNow(expiry);
    const timeoutId = setTimeout(setShortCodeExpired, codeDurationMilliseconds);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiry, setShortCodeExpired]);

  return {
    resetShortCode,
    getNewShortCode,
    setShortCodeClaimed,
    setShortCodeExpired,
    shortCode,
    shortCodeLoading,
    shortCodeState,
  };
};
